import { Button, Radio, RadioGroup, Text } from '@tunein/react-ui';
import { flow } from 'es-toolkit';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CLEAR_ALL_RECENTS_ENABLED } from 'src/common/constants/experiments/profile';
import {
  CHANGE_PASSWORD_BUTTON,
  CLEAR_ALL_RECENTS_BUTTON,
  CLEAR_ALL_RECENTS_DESCRIPTION,
  EMAIL_LABEL,
  FAVORITES_LABEL,
  GENERAL_SUBMIT_FAILED,
  GENERAL_SUBMIT_TEXT,
  NAME_LABEL,
  PASSWORD_LABEL,
  PRIVATE_PROFILE_LABEL,
  PUBLIC_PROFILE_LABEL,
  USERNAME_LABEL,
} from 'src/common/constants/localizations/settings';
import {
  EMAIL,
  EMAIL_ALREADY_EXISTS,
  EMAIL_REQUIRED,
  FULL_NAME_TOO_LONG,
  NAME,
  RECENTS,
  USERNAME,
  USERNAME_ALREADY_EXISTS,
  USERNAME_REQUIRED,
} from 'src/common/constants/localizations/shared';
import connectWithExperiments from 'src/common/decorators/connectWithExperiments';
import { changeUsername } from '../../../actions/auth';
import { updateUser } from '../../../actions/me';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import {
  composeValidators,
  isRequired,
  isValidEmail,
  isValidFullName,
} from '../../../utils/form/validation';
import FormInput from '../../shared/form/FormInput';
import TopLevelErrorContainer from '../../shared/form/TopLevelErrorContainer';
import commonSettingsCss from '../account-settings.module.scss';
import css from './general-settings.module.scss';

const fieldOrder = ['name', 'email', 'userName'];

class GeneralSettingsForm extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      updateUser: PropTypes.func.isRequired,
      changeUsername: PropTypes.func.isRequired,
    }),
    handleChangePasswordClick: PropTypes.func.isRequired,
    handleClearAllRecentsClick: PropTypes.func.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    [CLEAR_ALL_RECENTS_ENABLED]: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  onSubmit = async ({ name, email, isFollowingListPublic, userName }) => {
    const { actions, onSubmitSuccess } = this.props;

    try {
      await actions.updateUser({
        name,
        email: email.trim?.(),
        isFollowingListPublic,
      });
      await this.handleUserNameChange(userName.trim());
      onSubmitSuccess?.();
    } catch (error) {
      const responseStatusText = error?.response?.statusText;

      // TODO/NOTE: we need to have a different validation response from platform to help drive
      // how an error is connected to a particular field (e.g. OPML validation responses).
      if (/email/i.test(responseStatusText)) {
        return { email: [[EMAIL_ALREADY_EXISTS, [email]]] };
      }
      if (/invalid registration name/i.test(responseStatusText)) {
        return {
          name: [FULL_NAME_TOO_LONG],
        };
      }

      // testing against errorResponse because the API response for this field value comes from
      // Account.ashx, not profiles/me
      if (/username/i.test(error)) {
        return {
          userName: [[USERNAME_ALREADY_EXISTS, [userName]]],
        };
      }
      return { base: [GENERAL_SUBMIT_FAILED] };
    }
  };

  handleUserNameChange = (newUserName) => {
    const { initialValues, actions } = this.props;

    if (initialValues.userName !== newUserName) {
      return actions.changeUsername(newUserName);
    }
  };

  render() {
    const {
      handleChangePasswordClick,
      handleClearAllRecentsClick,
      initialValues,
      [CLEAR_ALL_RECENTS_ENABLED]: isClearAllRecentsEnabled,
    } = this.props;

    const { getLocalizedText } = this.context;

    return (
      <Form initialValues={initialValues} onSubmit={this.onSubmit} keepDirtyOnReinitialize>
        {({
          handleSubmit,
          errors,
          hasValidationErrors,
          pristine,
          submitting,
          submitErrors,
          touched,
          modifiedSinceLastSubmit,
        }) => (
          <form name="generalSettings" data-testid="generalSettingsForm" onSubmit={handleSubmit}>
            {errors && (!pristine || touched) && (
              <div className={commonSettingsCss.largeSection}>
                <div className={commonSettingsCss.errorContainer}>
                  <TopLevelErrorContainer
                    errorMessages={{
                      ...errors,
                      ...(!modifiedSinceLastSubmit && submitErrors),
                    }}
                    errorTextClassName={css.formErrorMessageText}
                    fieldOrder={fieldOrder}
                    touchedFields={touched}
                  />
                </div>
              </div>
            )}
            <div className={commonSettingsCss.mediumSection}>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <Text data-testid="nameLabel" weight="bold" size="-1" className={css.label}>
                        {getLocalizedText(NAME_LABEL)}
                      </Text>
                    </div>
                    <div className="col-xs-12">
                      <Field
                        id="name"
                        component={FormInput}
                        hideInlineError
                        className={css.input}
                        dataTestId="name"
                        name="name"
                        placeholder={getLocalizedText(NAME)}
                        validate={composeValidators(isValidFullName)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <Text data-testid="emailLabel" weight="bold" size="-1" className={css.label}>
                        {getLocalizedText(EMAIL_LABEL)}
                      </Text>
                    </div>
                    <div className="col-xs-12">
                      <Field
                        id="email"
                        component={FormInput}
                        hideInlineError
                        className={css.input}
                        dataTestId="email"
                        name="email"
                        autoCorrect="off"
                        autoCapitalize="none"
                        placeholder={getLocalizedText(EMAIL)}
                        validate={composeValidators(isRequired(EMAIL_REQUIRED), isValidEmail)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <Text
                        data-testid="usernameLabel"
                        weight="bold"
                        size="-1"
                        className={css.label}
                      >
                        {getLocalizedText(USERNAME_LABEL)}
                      </Text>
                    </div>
                    <div className="col-xs-12">
                      <Field
                        id="userName"
                        component={FormInput}
                        hideInlineError
                        className={css.input}
                        dataTestId="userName"
                        name="userName"
                        autoCorrect="off"
                        autoCapitalize="none"
                        placeholder={getLocalizedText(USERNAME)}
                        validate={composeValidators(isRequired(USERNAME_REQUIRED))}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <Text
                        data-testid="passwordLabel"
                        weight="bold"
                        size="-1"
                        className={css.label}
                      >
                        {getLocalizedText(PASSWORD_LABEL)}
                      </Text>
                    </div>
                    <div className="col-xs-12 col-sm-4">
                      <Button
                        data-testid="changePasswordButton"
                        id="changePasswordButton"
                        className={css.changePasswordButton}
                        variant="primary"
                        radius="full"
                        type="button"
                        onClick={handleChangePasswordClick}
                      >
                        {getLocalizedText(CHANGE_PASSWORD_BUTTON)}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              {isClearAllRecentsEnabled && (
                <div className={commonSettingsCss.section}>
                  <div className="row">
                    <div className={css.field}>
                      <div className="col-xs-12">
                        <Text data-testid="recentsLabel" weight="bold" size="-1">
                          {getLocalizedText(RECENTS)}
                        </Text>
                        <Text variant="secondary" size="-2" className={css.fieldDescription}>
                          {getLocalizedText(CLEAR_ALL_RECENTS_DESCRIPTION)}
                        </Text>
                      </div>
                      <div className="col-xs-12 col-sm-4">
                        <Button
                          data-testid="clearAllRecentsButton"
                          className={css.changePasswordButton}
                          variant="primary"
                          radius="full"
                          type="button"
                          onClick={handleClearAllRecentsClick}
                        >
                          {getLocalizedText(CLEAR_ALL_RECENTS_BUTTON)}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className={commonSettingsCss.largeSection}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <Text
                        data-testid="favoritesLabel"
                        weight="bold"
                        size="-1"
                        className={css.label}
                      >
                        {getLocalizedText(FAVORITES_LABEL)}
                      </Text>
                    </div>
                    <div className="col-xs-12">
                      <Field name="isFollowingListPublic">
                        {({ input }) => (
                          <RadioGroup
                            name={input.name}
                            value={input.value}
                            onValueChange={input.onChange}
                          >
                            <Radio
                              classNames={{
                                root: css.radioRoot,
                                indicator: css.radioIndicator,
                              }}
                              data-testid="privateProfileInd"
                              value="false"
                              label={getLocalizedText(PRIVATE_PROFILE_LABEL)}
                            />
                            <Radio
                              classNames={{
                                root: css.radioRoot,
                                indicator: css.radioIndicator,
                              }}
                              data-testid="publicProfileInd"
                              value="true"
                              label={getLocalizedText(PUBLIC_PROFILE_LABEL)}
                            />
                          </RadioGroup>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-4">
                  <Button
                    className={css.submitButtonGeneralSettings}
                    data-testid="generalSettingsSubmit"
                    id="generalSettingsSubmit"
                    variant="primary"
                    radius="full"
                    type="submit"
                    disabled={submitting || hasValidationErrors || pristine}
                  >
                    {getLocalizedText(GENERAL_SUBMIT_TEXT)}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Form>
    );
  }
}

function mapStateToProps(state) {
  const {
    me: { details },
  } = state;

  const initialValues = {
    name: details.title,
    userName: details.userName,
    email: details.email,
    // To handle cases where isFollowingListPublic does not have an associated boolean value
    isFollowingListPublic: details.isFollowingListPublic?.toString(),
  };

  return { initialValues };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateUser,
        changeUsername,
      },
      dispatch,
    ),
  };
}

export default flow(
  connectWithExperiments([CLEAR_ALL_RECENTS_ENABLED]),
  connect(mapStateToProps, mapDispatchToProps),
)(GeneralSettingsForm);
